import { avatarStyle, initialsFor } from '../lib/identity.js';

export type AvatarSize = 'sm' | 'md' | 'lg';

/**
 * A person.
 *
 * The ring is driven by their live audio level rather than a boolean, so a
 * quiet room looks quiet instead of strobing on and off every time someone
 * crosses a threshold. That continuous response is most of what makes a stage
 * feel alive rather than like a list of names.
 */
export function Avatar({
  sub,
  name,
  size = 'md',
  level = 0,
  speaking = false,
  muted = false,
}: {
  sub: string;
  name: string;
  size?: AvatarSize;
  level?: number;
  speaking?: boolean;
  muted?: boolean;
}) {
  // Compressed rather than linear: voice levels cluster low, so a raw value
  // barely moves the ring. The floor keeps a speaking ring visible at all.
  const intensity = speaking ? Math.min(1, 0.35 + Math.sqrt(level) * 1.6) : 0;

  return (
    <div
      class={`avatar avatar--${size}${speaking ? ' avatar--speaking' : ''}`}
      style={{ ...avatarStyle(sub), '--level': String(intensity) }}
    >
      <div class="avatar__ring" aria-hidden="true" />
      <div class="avatar__face">{initialsFor(name || sub)}</div>
      {muted ? (
        <span class="avatar__badge" title="Muted">
          <MutedIcon />
        </span>
      ) : null}
    </div>
  );
}

/* Line icon, 1.75 stroke, rounded terminals — Constitution §3 iconography. */
function MutedIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}
